import { ValidationError, SuggestedAction, YardMapFocusContext, EntityType } from '../types/interchange'
import { Map as MapIcon } from 'lucide-react'
import { getHighlightConfig, getLabelOverride } from '../utils/errorHighlightRegistry'

interface SafetyAlertsPanelProps {
  validationErrors: ValidationError[]
  suggestedActions?: SuggestedAction[]
  onViewOnMap?: (ctx: YardMapFocusContext) => void
  activeTheme?: 'midnight' | 'foundry' | 'daylight' | 'cyber'
}

export default function SafetyAlertsPanel({ validationErrors, suggestedActions, onViewOnMap, activeTheme = 'midnight' }: SafetyAlertsPanelProps) {
  const SAFETY_ERROR_CODES = ['SAFETY_LOCKOUT_BLUE_FLAG', 'SAFETY_HAZARD_FOUL_ZONE']

  // Utility: Extract focus context from a validation error
  function extractFocusContext(error: ValidationError, activeTheme: 'midnight' | 'foundry' | 'daylight' | 'cyber' = 'midnight'): YardMapFocusContext | null {
    const spatialCodes = [
      'PATH_BLOCKED', 'SAFETY_LOCKOUT_BLUE_FLAG', 'CAPACITY_EXCEEDED',
      'FOUL_ZONE_VIOLATION', 'LOCOMOTIVE_NOT_ON_SOURCE_TRACK'
    ]

    if (!error.code || !spatialCodes.includes(error.code)) return null

    // Use structured data from ValidationError instead of parsing message
    const switchId = error.metadata?.switchId as string | undefined
    const trackId = error.trackId || error.metadata?.trackId as string | undefined

    const isSwitchError = !!switchId || error.code === 'PATH_BLOCKED'
    const targetEntityType: EntityType = isSwitchError ? 'SWITCH' : 'TRACK_SEGMENT'
    const targetEntityName = switchId ?? trackId ?? error.field ?? 'Unknown'

    // Compute highlight config from registry
    const highlightConfig = getHighlightConfig(error.code, activeTheme)
    const labelOverride = getLabelOverride(error.code)

    return {
      targetEntityType,
      targetEntityName,
      errorCode: error.code,
      errorMessage: error.message,
      highlightConfig: {
        ...highlightConfig,
        labelOverride,
      },
      // Extract related entities from metadata if available
      relatedEntities: error.metadata?.relatedEntities as Array<{ id: string; type: EntityType; role: 'CAUSE' | 'VICTIM' }> | undefined,
      // Extract simulation data for dry-run scenarios
      simulationData: error.metadata?.simulationData as { virtualPositions?: Record<string, number> } | undefined,
    }
  }

  const safetyErrors = validationErrors.filter(error =>
    error.code && SAFETY_ERROR_CODES.includes(error.code)
  )

  const generalErrors = validationErrors.filter(error =>
    !error.code || !SAFETY_ERROR_CODES.includes(error.code)
  )

  const generalWarnings = generalErrors.filter(error =>
    error.severity === 'WARNING' || error.severity === 'INFO'
  )

  const generalErrorsOnly = generalErrors.filter(error =>
    error.severity === 'ERROR' || error.severity === 'CRITICAL'
  )

  const getSafetyIcon = (code?: string) => {
    switch (code) {
      case 'SAFETY_LOCKOUT_BLUE_FLAG':
        return '🚩'
      case 'SAFETY_HAZARD_FOUL_ZONE':
        return '⚠️'
      default:
        return '⛔'
    }
  }

  const getSeverityIcon = (severity?: string) => {
    switch (severity) {
      case 'CRITICAL':
        return '🔴'
      case 'ERROR':
        return '⛔'
      case 'WARNING':
        return '⚠️'
      case 'INFO':
        return 'ℹ️'
      default:
        return '⚠️'
    }
  }

  const getSafetyColor = (code?: string) => {
    switch (code) {
      case 'SAFETY_LOCKOUT_BLUE_FLAG':
        return 'border-red-500 bg-red-200 midnight:bg-red-900 cyber:bg-red-900 foundry:bg-red-900/30 daylight:bg-red-200'
      case 'SAFETY_HAZARD_FOUL_ZONE':
        return 'border-orange-500 bg-orange-200 midnight:bg-orange-900 cyber:bg-orange-900 foundry:bg-orange-900/30 daylight:bg-orange-200'
      default:
        return 'border-red-500 bg-red-200 midnight:bg-red-900 cyber:bg-red-900 foundry:bg-red-900/30 daylight:bg-red-200'
    }
  }

  const getHumanReadableMessage = (code?: string, message?: string) => {
    switch (code) {
      case 'SAFETY_LOCKOUT_BLUE_FLAG':
        return 'Blue Flag Zone Active - Personnel safety override in effect. Track is locked for maintenance or worker protection.'
      case 'SAFETY_HAZARD_FOUL_ZONE':
        return 'Foul Zone Violation - Track clearance insufficient for safe movement. Switch turnout protection zone violation detected.'
      default:
        return message || 'Safety violation detected'
    }
  }

  return (
    <div className="flex flex-col h-full">
      <h3 className="font-bold text-muted-foreground shrink-0">Contextual Safety Alerts</h3>

      <div className="flex-1 overflow-y-auto">
        {/* Safety Violations */}
        {safetyErrors.length > 0 && (
          <div className="space-y-3 pt-4">
            {safetyErrors.map((error) => (
              <div
                key={error.id}
                className={`p-4 border-l-4 rounded-lg ${getSafetyColor(error.code)}`}
              >
                <div className="flex items-start gap-3">
                  <span className="text-2xl">{getSafetyIcon(error.code)}</span>
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-1">
                      <div className="font-semibold text-foreground">
                        {error.code?.replace(/_/g, ' ')}
                      </div>
                      {onViewOnMap && (() => {
                        const ctx = extractFocusContext(error, activeTheme)
                        return ctx ? (
                          <button
                            onClick={() => onViewOnMap(ctx)}
                            className="ml-2 px-2 py-0.5 text-xs rounded border border-blue-400 text-blue-400 hover:bg-blue-400/10 transition-colors flex items-center gap-1 shrink-0"
                            title="View location on yard map"
                          >
                            <MapIcon className="w-3 h-3" /> View on Map
                          </button>
                        ) : null
                      })()}
                    </div>
                    <div className="text-sm text-foreground mb-2">
                      {getHumanReadableMessage(error.code, error.message)}
                    </div>
                    {error.trackId && (
                      <div className="text-xs text-muted-foreground">
                        Affected Track: {error.trackId}
                      </div>
                    )}
                    {error.timestamp && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {new Date(error.timestamp).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* General Errors */}
        {generalErrorsOnly.length > 0 && (
          <div className="space-y-2 pt-4">
            <h4 className="text-sm font-semibold text-muted-foreground">Errors</h4>
            {generalErrorsOnly.map((error) => (
              <div
                key={error.id}
                className="p-3 border border-red-500/50 rounded-lg bg-red-50/50 midnight:bg-red-900/50 cyber:bg-red-900/50 foundry:bg-red-900/30 daylight:bg-red-50/50"
              >
                <div className="flex justify-between items-start mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{getSeverityIcon(error.severity)}</span>
                    <span className="text-sm font-medium text-foreground">
                      {error.code || error.field || 'Validation Error'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      error.severity === 'CRITICAL' ? 'bg-red-100 text-red-800 midnight:bg-red-900 midnight:text-red-200 cyber:bg-red-900 cyber:text-red-200 foundry:bg-red-900/50 foundry:text-red-300 daylight:bg-red-100 daylight:text-red-800' :
                      'bg-orange-100 text-orange-800 midnight:bg-orange-900 midnight:text-orange-200 cyber:bg-orange-900 cyber:text-orange-200 foundry:bg-orange-900/50 foundry:text-orange-300 daylight:bg-orange-100 daylight:text-orange-800'
                    }`}>
                      {error.severity}
                    </span>
                    {onViewOnMap && (() => {
                      const ctx = extractFocusContext(error, activeTheme)
                      return ctx ? (
                        <button
                          onClick={() => onViewOnMap(ctx)}
                          className="ml-2 px-2 py-0.5 text-xs rounded border border-blue-400 text-blue-400 hover:bg-blue-400/10 transition-colors flex items-center gap-1 shrink-0"
                          title="View location on yard map"
                        >
                          <MapIcon className="w-3 h-3" /> View on Map
                        </button>
                      ) : null
                    })()}
                  </div>
                </div>
                <div className="text-sm text-foreground">{error.message}</div>
                {error.timestamp && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {new Date(error.timestamp).toLocaleString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* General Warnings */}
        {generalWarnings.length > 0 && (
          <div className="space-y-2 pt-4">
            <h4 className="text-sm font-semibold text-muted-foreground">Warnings</h4>
            {generalWarnings.map((error) => (
              <div
                key={error.id}
                className="p-3 border border-yellow-500/50 rounded-lg bg-yellow-50/50 midnight:bg-yellow-900/50 cyber:bg-yellow-900/50 foundry:bg-yellow-900/30 daylight:bg-yellow-50/50"
              >
                <div className="flex justify-between items-start mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{getSeverityIcon(error.severity)}</span>
                    <span className="text-sm font-medium text-foreground">
                      {error.code || error.field || 'Validation Warning'}
                    </span>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-800 midnight:bg-yellow-900 midnight:text-yellow-200 cyber:bg-yellow-900 cyber:text-yellow-200 foundry:bg-yellow-900/50 foundry:text-yellow-300 daylight:bg-yellow-100 daylight:text-yellow-800">
                    {error.severity}
                  </span>
                </div>
                <div className="text-sm text-foreground">{error.message}</div>
                {error.timestamp && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {new Date(error.timestamp).toLocaleString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* No Errors State */}
        {validationErrors.length === 0 && (
          <div className="flex flex-col items-center justify-center min-h-full text-center py-8">
            <div className="text-4xl mb-3">✅</div>
            <div className="text-foreground font-medium">No Safety Violations</div>
            <div className="text-sm text-muted-foreground mt-1">
              All validation checks passed
            </div>
          </div>
        )}

        {/* Suggested Actions Section */}
        {suggestedActions && suggestedActions.length > 0 && (
          <div className="mt-4 space-y-2">
            <h4 className="text-sm font-semibold text-muted-foreground">Suggested Actions</h4>
            <div className="space-y-2">
              {(() => {
                // Deduplicate suggestions by actionType to avoid redundant UI blocks
                const uniqueActions = Array.from(
                  new Map(suggestedActions.map(action => [action.actionType, action])).values()
                )
                return uniqueActions.map((action, index) => (
                  <div
                    key={`${action.actionType}-${index}`}
                    className="p-3 border border-blue-500 bg-blue-50 midnight:bg-blue-950/50 cyber:bg-blue-950/50 foundry:bg-blue-900/30 daylight:bg-blue-50 rounded-lg hover:bg-blue-100 midnight:hover:bg-blue-900/50 cyber:hover:bg-blue-900/50 foundry:hover:bg-blue-900/50 daylight:hover:bg-blue-100 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-lg">💡</span>
                      <div className="flex-1">
                        <div className="text-xs font-medium text-blue-700 midnight:text-blue-400 cyber:text-blue-400 foundry:text-blue-300 daylight:text-blue-700 mb-1">
                          {action.actionType.replace(/_/g, ' ')}
                        </div>
                        <div className="text-sm text-foreground">
                          {action.description}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
